import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { listAudit, type AuditEntry, type Page } from "../../../features/admin/api/adminApi";
import { PageHeader } from "./_shared";

const fmt = (d: string) => new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminAudit() {
  const [data, setData] = useState<Page<AuditEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    listAudit({ page, limit: 40 }).then(setData).catch(() => undefined).finally(() => setLoading(false));
  }, [page]);

  return (
    <>
      <PageHeader title="Audit log" subtitle={data ? `${data.total} recorded actions` : undefined} />
      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex h-40 items-center justify-center text-ink-400"><Loader2 className="animate-spin" /></div>
        ) : (
          <div className="surface-card overflow-x-auto p-0">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-ink-100 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
                  <th className="px-4 py-3">When</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Target</th>
                </tr>
              </thead>
              <tbody>
                {(data?.rows ?? []).map((l) => (
                  <tr key={l._id} className="border-b border-ink-100 last:border-0">
                    <td className="whitespace-nowrap px-4 py-3 text-ink-500">{fmt(l.createdAt)}</td>
                    <td className="px-4 py-3">{l.actor ? l.actor.email : "system"}</td>
                    <td className="px-4 py-3"><span className="rounded-md bg-ink-100 px-2 py-0.5 font-mono text-[11px] text-ink-700">{l.action}</span></td>
                    <td className="px-4 py-3 text-ink-600">{l.target?.label ?? (l.target?.type ? `${l.target.type} ${l.target.id ?? ""}` : "—")}</td>
                  </tr>
                ))}
                {(data?.rows.length ?? 0) === 0 && (
                  <tr><td colSpan={4} className="px-4 py-10 text-center text-ink-400">No audit entries yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {data && data.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2 text-[12.5px]">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Prev</button>
            <span className="text-ink-500">Page {page} of {data.totalPages}</span>
            <button disabled={page >= data.totalPages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border border-ink-200 px-3 py-1.5 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </>
  );
}
